import { useCallback, useEffect, useState } from "react";
import { KeyRound, RefreshCw, ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { navForRole } from "@/components/roleNav";
import { TwoFactorSetup } from "@/components/TwoFactorSetup";import { SecurityEventsPanel } from "@/components/SecurityEventsPanel";
import { twofaService } from "@/services/twofaService";
import { errorMessage } from "@/services/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const card = "rounded-md border border-[#22252F] bg-[#12141A] p-5";

export default function SecurityPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(false);
  const [password, setPassword] = useState("");
  const [codes, setCodes] = useState(null);

  const load = useCallback(async () => {
    try {
      setStatus(await twofaService.status());
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try {
      const res = await fn(password);
      setPassword("");
      if (res.recovery_codes) setCodes(res.recovery_codes);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <AppShell nav={navForRole(user?.role)} title="Segurança" subtitle="Autenticação em dois fatores da sua conta interna">
      <div data-testid="security-page" className="max-w-2xl space-y-6">
        <div className={card}>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="flex items-center gap-2 text-sm text-white">
                {status?.enabled ? <ShieldCheck className="h-4 w-4 text-[#10B981]" /> : <ShieldOff className="h-4 w-4 text-[#F59E0B]" />}
                Autenticação em dois fatores (TOTP)
              </p>
              <p data-testid="twofa-status" className="mt-1 text-sm text-[#94A3B8]">
                {status?.enabled ? "Ativa" : "Inativa"}
                {status?.mandatory ? " · obrigatória nesta conta" : ""}
                {status?.enabled ? ` · ${status.recovery_codes_left} códigos de recuperação disponíveis` : ""}
              </p>
            </div>
            {!status?.enabled ? (
              <Button data-testid="start-twofa-setup" onClick={() => setSetup(true)}
                      className="rounded-md bg-[#2563EB] text-xs uppercase tracking-[0.15em] hover:bg-[#1D4ED8]">
                Ativar
              </Button>
            ) : null}
          </div>
        </div>

        {setup && !status?.enabled ? (
          <div className={card}>
            <TwoFactorSetup onDone={() => { setSetup(false); load(); }} />
          </div>
        ) : null}

        {status?.enabled ? (
          <div className={`${card} space-y-4`}>
            <p className="flex items-center gap-2 text-sm text-white"><KeyRound className="h-4 w-4" /> Operações protegidas</p>
            <Input type="password" data-testid="security-password-input" value={password}
                   onChange={(e) => setPassword(e.target.value)} placeholder="Confirme a sua palavra-passe"
                   className="h-11 rounded-md border-[#22252F] bg-[#0F1116] text-white" />
            <div className="flex flex-wrap gap-3">
              <Button data-testid="regenerate-recovery-codes" disabled={!password}
                      onClick={() => act(twofaService.regenerate, "Novos códigos gerados")}
                      className="rounded-md bg-[#10B981] text-xs uppercase tracking-[0.15em] text-[#04150F] hover:bg-[#0EA271]">
                <RefreshCw className="mr-2 h-4 w-4" /> Novos códigos de recuperação
              </Button>
              {!status?.mandatory ? (
                <Button data-testid="disable-twofa" disabled={!password}
                        onClick={() => act(twofaService.disable, "2FA desativada")}
                        className="rounded-md bg-[#EF4444] text-xs uppercase tracking-[0.15em] hover:bg-[#DC2626]">
                  <ShieldOff className="mr-2 h-4 w-4" /> Desativar 2FA
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}

        {codes ? (
          <div className={`${card} space-y-3`} data-testid="new-recovery-codes">
            <p className="text-sm text-white">Novos códigos de recuperação (os antigos deixaram de funcionar):</p>
            <div className="grid grid-cols-2 gap-2 font-mono text-sm text-[#60A5FA]">
              {codes.map((c) => <span key={c}>{c}</span>)}
            </div>
          </div>
        ) : null}

        {user?.role === "admin" ? <SecurityEventsPanel /> : null}
      </div>
    </AppShell>
  );
}
